"use client";

// Top-Navigation mit Headroom-Verhalten (hide-on-down / show-on-up) + Floating Demo-CTA.
// Pattern inspiriert von relyz.ch: Nav verschwindet beim Runterscrollen, kommt beim Rauf-Scrollen zurück.
// Der „Demo buchen"-Button klebt zusätzlich als kleiner Floating-Tab rechts, nachdem der Hero gescrollt ist.

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Menu, X, LogOut } from "lucide-react";

const BOOKINGS_URL = "https://outlook.office.com/book/OrchestraDemo@danexis.com/";
// Ziel der App — interner Pfad, da Landing und App jetzt im selben Next.js-
// Projekt leben. proxy.ts schützt /dashboard und alle weiteren App-Routen,
// und leitet auf /login um, falls noch nicht angemeldet.
const APP_URL = "/dashboard";

const LINKS = [
  { href: "/#produkt", label: "Produkt" },
  { href: "/#workflow", label: "Workflow" },
  { href: "/#features", label: "Bereiche" },
  { href: "/start", label: "Start mit Orchestra" },
];

// Headroom: wann soll die Nav verstecken / zeigen?
const SHOW_NEAR_TOP_PX = 80;    // in den ersten 80px immer sichtbar
const SCROLL_THRESHOLD = 6;      // Delta, bevor eine Richtungsänderung zählt (verhindert Jitter)
const SOLID_AFTER_PX = 80;       // ab wann Nav von transparent zu solid wechselt (light-Variante)

type NavVariant = "default" | "light";

interface NavProps {
  variant?: NavVariant;
  /** Wenn true: Nav zeigt "Orchestra öffnen" + Abmelden statt eines reinen
   * Marketing-CTAs. Wird von der Server-Wrapper-Page über Cookie ermittelt. */
  isAuthenticated?: boolean;
}

export function Nav({ variant = "default", isAuthenticated = false }: NavProps = {}) {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [showFloatCTA, setShowFloatCTA] = useState(false);
  const [scrolledPast, setScrolledPast] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const lastYRef = useRef(0);

  async function handleLogout() {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } catch {
      // selbst bei Fehlschlag harter Reload — der Cookie ist serverseitig
      // im Zweifel ungültig, der Reload klärt den Zustand.
    }
    // Harter Reload, damit alle Layouts und SWR-Caches frisch sind.
    window.location.href = "/";
  }

  // Light-Variante nur im oberen Bereich transparent; nach Scroll solid
  const isTransparent = variant === "light" && !scrolledPast && !open;

  // Scroll-Listener mit rAF-Drosselung
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        const last = lastYRef.current;

        // Floating-CTA zeigen sobald man den Hero verlassen hat (~ 600px)
        setShowFloatCTA(y > 600);

        // Transparent / solid Umschlag (für light-Variante)
        setScrolledPast(y > SOLID_AFTER_PX);

        // In den ersten 80px immer sichtbar
        if (y < SHOW_NEAR_TOP_PX) {
          setHidden(false);
          lastYRef.current = y;
          return;
        }

        const delta = y - last;
        if (Math.abs(delta) < SCROLL_THRESHOLD) return; // ignoriere Mikro-Wackler

        if (delta > 0) setHidden(true);   // runter → verstecken
        else setHidden(false);             // rauf → zeigen

        lastYRef.current = y;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  // ESC schließt Mobile-Menü
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Body-Scroll bei offenem Mobile-Menü blockieren
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <nav
        className={`fixed left-0 right-0 z-50 transition-[transform,background-color,border-color,backdrop-filter] duration-300 ease-out ${
          isTransparent
            ? "bg-transparent border-b border-transparent"
            : "backdrop-blur-xl bg-[rgba(250,250,247,0.8)] border-b border-[var(--color-line)]"
        } ${hidden && !open ? "-translate-y-full" : "translate-y-0"}`}
        style={{ top: "var(--banner-h, 0px)" }}
      >
      <div className="max-w-[1280px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between gap-4 md:gap-8">
        {/* Logo */}
        <div
          className={`flex items-center gap-2.5 font-light tracking-tight text-[18px] shrink-0 transition-colors duration-300 ${
            isTransparent ? "text-white" : "text-[var(--color-ink)]"
          }`}
        >
          <Image
            src="/Logoicon.svg"
            alt=""
            width={32}
            height={32}
            className="w-8 h-8 transition-[filter] duration-300"
            style={isTransparent ? { filter: "brightness(0) invert(1)" } : undefined}
            priority
          />
          Orchestra
        </div>

        {/* Desktop: Sektions-Links */}
        <div
          className={`hidden lg:flex gap-7 text-sm transition-colors duration-300 ${
            isTransparent ? "text-white/80" : "text-[var(--color-ink-soft)]"
          }`}
        >
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className={`transition-colors focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-blue)] rounded-sm ${
                isTransparent ? "hover:text-white" : "hover:text-[var(--color-ink)]"
              }`}
            >
              {l.label}
            </a>
          ))}
        </div>

        {/* Rechts: Endorsement + CTA + Burger */}
        <div className="flex items-center gap-3 md:gap-5">
          {/* Orchestra-CTA — führt zur App (/dashboard). Eingeloggte sehen
              zusätzlich einen Abmelden-Button; anonyme zusätzlich „Anmelden". */}
          <a
            href={APP_URL}
            aria-label={isAuthenticated ? "Zur Orchestra-App" : "Orchestra-App entdecken"}
            className={`hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13.5px] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-blue)] ${
              isTransparent
                ? "bg-white text-[var(--color-ink)] hover:bg-[var(--color-blue)] hover:text-white"
                : "bg-[var(--color-ink)] text-white hover:bg-[var(--color-blue)]"
            }`}
          >
            {isAuthenticated ? "Orchestra öffnen" : "Orchestra"} <span aria-hidden>→</span>
          </a>
          {isAuthenticated ? (
            <button
              type="button"
              onClick={handleLogout}
              disabled={isLoggingOut}
              aria-label="Abmelden"
              title="Abmelden"
              className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[13px] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-blue)] disabled:opacity-50 ${
                isTransparent
                  ? "border border-white/30 text-white hover:bg-white/10"
                  : "border border-[var(--color-line)] text-[var(--color-ink-soft)] hover:text-[var(--color-ink)] hover:bg-white"
              }`}
            >
              <LogOut size={14} strokeWidth={1.75} />
              {isLoggingOut ? "Abmelden …" : "Abmelden"}
            </button>
          ) : (
            <a
              href="/login"
              className={`hidden sm:inline-flex items-center text-[13px] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-blue)] ${
                isTransparent
                  ? "text-white/80 hover:text-white"
                  : "text-[var(--color-ink-soft)] hover:text-[var(--color-ink)]"
              }`}
            >
              Anmelden
            </a>
          )}

          {/* Danexis-Logo (Endorsement) rechts. Im transparenten Modus
              ausgeblendet, weil das JPG keinen sauberen Weiss-Modus hat. */}
          <a
            href="https://www.danexis.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Danexis (Noser Group)"
            className={`hidden md:flex items-center transition-opacity duration-300 ${
              isTransparent ? "opacity-0 pointer-events-none" : "opacity-100"
            }`}
          >
            <Image
              src="/danexis-full.jpg"
              alt="Danexis — Noser Group"
              width={120}
              height={28}
              className="h-7 w-auto"
            />
          </a>

          {/* Burger (nur unter lg) */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={open}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-blue)] ${
              isTransparent
                ? "border border-white/30 bg-white/10 text-white hover:bg-white/20"
                : "border border-[var(--color-line)] bg-white/60 hover:bg-white"
            }`}
          >
            {open ? <X size={18} strokeWidth={1.75} /> : <Menu size={18} strokeWidth={1.75} />}
          </button>
        </div>
      </div>

      {/* Mobile Slide-Down-Panel */}
      <div
        className={`lg:hidden absolute left-0 right-0 top-full bg-[var(--color-bg)] border-b border-[var(--color-line)] shadow-lg overflow-hidden transition-all duration-300 ease-out ${
          open ? "max-h-[520px] opacity-100" : "max-h-0 opacity-0 pointer-events-none"
        }`}
      >
        <div className="px-6 py-8 flex flex-col gap-1">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="py-3 text-[18px] font-medium text-[var(--color-ink)] hover:text-[var(--color-blue)] transition-colors border-b border-[var(--color-line)] last:border-b-0"
            >
              {l.label}
            </a>
          ))}
          <a
            href={APP_URL}
            onClick={() => setOpen(false)}
            className="mt-6 bg-[var(--color-ink)] text-white px-6 py-4 rounded-full text-[15px] font-medium inline-flex items-center justify-center gap-2 hover:bg-[var(--color-blue)] transition-colors"
          >
            Orchestra öffnen <span>→</span>
          </a>
          {isAuthenticated ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                handleLogout();
              }}
              disabled={isLoggingOut}
              className="mt-3 border border-[var(--color-line)] text-[var(--color-ink)] px-6 py-4 rounded-full text-[15px] font-medium inline-flex items-center justify-center gap-2 hover:bg-[var(--color-bg)] transition-colors disabled:opacity-50"
            >
              <LogOut size={16} strokeWidth={1.75} />
              {isLoggingOut ? "Abmelden …" : "Abmelden"}
            </button>
          ) : (
            <a
              href="/login"
              onClick={() => setOpen(false)}
              className="mt-3 border border-[var(--color-line)] text-[var(--color-ink)] px-6 py-4 rounded-full text-[15px] font-medium inline-flex items-center justify-center gap-2 hover:bg-[var(--color-bg)] transition-colors"
            >
              Anmelden
            </a>
          )}
          <a
            href={BOOKINGS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="mt-3 border border-[var(--color-line)] text-[var(--color-ink)] px-6 py-4 rounded-full text-[15px] font-medium inline-flex items-center justify-center gap-2 hover:bg-[var(--color-bg)] transition-colors"
          >
            Demo buchen <span>→</span>
          </a>
        </div>
      </div>
    </nav>

    {/* Floating Demo-CTA — klebt rechts am Rand, erscheint sobald man den Hero verlassen hat */}
    <a
      href={BOOKINGS_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Demo buchen"
      className={`hidden md:flex fixed right-6 bottom-8 z-40 items-center gap-2 bg-[var(--color-ink)] text-white px-5 py-3 rounded-full text-[14px] font-medium shadow-lg hover:bg-[var(--color-blue)] transition-all duration-300 ${
        showFloatCTA
          ? "translate-y-0 opacity-100"
          : "translate-y-4 opacity-0 pointer-events-none"
      }`}
      style={{ boxShadow: "0 16px 40px -8px rgba(10,10,15,0.3)" }}
    >
      Demo buchen <span>→</span>
    </a>
    </>
  );
}
